// Image ↔ floor ("board plane") mapping.
//
// Board frame, in centimetres: origin where the stringer crosses the rear
// strip, +y along the stringer toward the nose, +x to the right when facing
// the nose (right-handed seen from above). Both calibration methods produce a
// homography from *isotropic* image coordinates (x·aspect, y) to this frame, so
// everything downstream is one code path.
//
// The mapping is only exact for points on the floor. Heel and toe landmarks
// sit a few centimetres above it, so absolute positions carry a small,
// view-dependent bias. Targets are captured through the same mapping from the
// same camera, so that bias largely cancels when attempts are compared with
// the reference stance — which is how the analysis uses it.

import {
  applyH, dist, homographyFrom4, invert3, isConvex, jacobianDet, mid, norm, polygonArea, segmentsIntersect, sub,
  type Mat3, type Vec2,
} from './geometry';

export type Stance = 'regular' | 'goofy';

export interface TapeLayout {
  /** Normalized image positions of the rear strip's two ends (any order). */
  rear: [Vec2, Vec2];
  /** Normalized image positions of the front strip's two ends (any order). */
  front: [Vec2, Vec2];
  /** Length of each crosswise strip, cm. */
  stripLength: number;
  /** Distance between the strips' centrelines, cm. */
  gap: number;
}

export interface PlaneMapping {
  kind: 'tape' | 'stance';
  /** Frame width / height the calibration was made at. */
  aspect: number;
  /** Isotropic image coords → board plane (cm). */
  H: Mat3;
  Hinv: Mat3;
}

export const toIso = (p: Vec2, aspect: number): Vec2 => ({ x: p.x * aspect, y: p.y });
export const fromIso = (p: Vec2, aspect: number): Vec2 => ({ x: p.x / aspect, y: p.y });

export function toPlane(m: PlaneMapping, pNorm: Vec2): Vec2 {
  return applyH(m.H, toIso(pNorm, m.aspect));
}

export function toImage(m: PlaneMapping, pPlane: Vec2): Vec2 {
  return fromIso(applyH(m.Hinv, pPlane), m.aspect);
}

/** +1 when the rider's toes point toward +x (regular), −1 for goofy. */
export const toeSideSign = (stance: Stance): 1 | -1 => (stance === 'regular' ? 1 : -1);

export interface TapeMappingResult {
  mapping: PlaneMapping | null;
  problems: string[];
  warnings: string[];
}

export function mappingFromTape(tape: TapeLayout, aspect: number): TapeMappingResult {
  const problems: string[] = [];
  const warnings: string[] = [];
  const [r0, r1] = tape.rear.map((p) => toIso(p, aspect));
  let [f0, f1] = tape.front.map((p) => toIso(p, aspect));
  const all = [r0, r1, f0, f1];

  for (let i = 0; i < 4; i++)
    for (let j = i + 1; j < 4; j++)
      if (dist(all[i], all[j]) < 0.015) problems.push('Two of the marked points are on top of each other.');
  if (problems.length) return { mapping: null, problems: [...new Set(problems)], warnings };

  // Pair each front end with the rear end on the same side of the stringer:
  // the two "rails" r0→f0 and r1→f1 must not cross.
  if (segmentsIntersect(r0, f0, r1, f1)) [f0, f1] = [f1, f0];
  const quad = [r0, r1, f1, f0];
  if (!isConvex(quad)) {
    return { mapping: null, problems: ['The four points don’t form the H shape. Check you marked the back strip first, then the front strip.'], warnings };
  }
  if (Math.abs(polygonArea(quad)) < 0.004 * aspect) {
    warnings.push('The tape looks very small in the frame. Move the phone closer for more precise measurements.');
  }

  const L = tape.stripLength;
  const D = tape.gap;
  const solve = (flip: 1 | -1) =>
    homographyFrom4([r0, r1, f0, f1], [
      { x: (-flip * L) / 2, y: 0 },
      { x: (flip * L) / 2, y: 0 },
      { x: (-flip * L) / 2, y: D },
      { x: (flip * L) / 2, y: D },
    ]);

  // A camera above the floor sees a mirror image of the top-down view (image y
  // points down), so the correctly handed mapping has a negative Jacobian.
  const centre = mid(mid(r0, r1), mid(f0, f1));
  let H = solve(1);
  if (H && jacobianDet(H, centre) > 0) H = solve(-1);
  const Hinv = H && invert3(H);
  if (!H || !Hinv) return { mapping: null, problems: ['Those points can’t be turned into a floor plane. Try marking them again.'], warnings };

  const mapping: PlaneMapping = { kind: 'tape', aspect, H, Hinv };
  // Points below the horizon map to finite floor positions; the image's bottom
  // edge must be floor in any sensible setup.
  const near = toPlane(mapping, { x: 0.5, y: 1 });
  if (!Number.isFinite(near.x) || Math.abs(near.x) > 5000) {
    return { mapping: null, problems: ['The camera view doesn’t look down at the floor. Tilt the phone down a little.'], warnings };
  }
  return { mapping, problems, warnings };
}

/**
 * Tape-free fallback: a similarity transform anchored on the reference stance
 * (rear foot → origin, front foot → (0, stanceWidth)). It does not correct
 * perspective, so distances are approximate; directions are still meaningful
 * because attempts are compared with a reference seen from the same camera.
 */
export function mappingFromStance(rearNorm: Vec2, frontNorm: Vec2, stanceWidth: number, aspect: number): PlaneMapping {
  const o = toIso(rearNorm, aspect);
  const f = toIso(frontNorm, aspect);
  const ey = norm(sub(f, o));
  const ex = { x: -ey.y, y: ey.x }; // chosen so the map is orientation-reversing, like the tape homography
  const s = stanceWidth / Math.max(1e-6, dist(o, f));
  const H: Mat3 = [
    s * ex.x, s * ex.y, -s * (ex.x * o.x + ex.y * o.y),
    s * ey.x, s * ey.y, -s * (ey.x * o.x + ey.y * o.y),
    0, 0, 1,
  ];
  return { kind: 'stance', aspect, H, Hinv: invert3(H)! };
}

/** Which side of the stringer the camera is on (+1 = +x), judged from the nearest floor in view. */
export function cameraSide(m: PlaneMapping): 1 | -1 {
  return toPlane(m, { x: 0.5, y: 1 }).x >= 0 ? 1 : -1;
}

/** Apparent image scale at a floor point: image-height fraction per centimetre. */
export function imageScaleAt(m: PlaneMapping, plane: Vec2): number {
  const det = Math.abs(jacobianDet(m.Hinv, plane));
  return Math.sqrt(det);
}

/** Endpoints of the stringer and strips projected into the image, for overlays. */
export function tapeOverlay(m: PlaneMapping, tape: { stripLength: number; gap: number }) {
  const L = tape.stripLength / 2;
  const D = tape.gap;
  const seg = (a: Vec2, b: Vec2) => [toImage(m, a), toImage(m, b)] as const;
  return {
    stringer: seg({ x: 0, y: -0.6 * D }, { x: 0, y: 1.9 * D }),
    rear: seg({ x: -L, y: 0 }, { x: L, y: 0 }),
    front: seg({ x: -L, y: D }, { x: L, y: D }),
  };
}
